import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Job } from '@/types/job';
import { toast } from 'sonner';

interface AISearchResult {
  matchedJobIds: string[];
  reasoning: string;
  confidence: number;
}

export const useAIJobSearch = () => {
  const [isSearching, setIsSearching] = useState(false);
  const [reasoning, setReasoning] = useState('');
  const [confidence, setConfidence] = useState(0);

  const searchJobs = async (allJobs: Job[], query: string): Promise<Job[]> => {
    if (!query.trim()) {
      setReasoning('');
      setConfidence(0);
      return allJobs;
    }

    setIsSearching(true);
    setReasoning('');
    setConfidence(0);

    try {
      const { data, error } = await supabase.functions.invoke('ai-job-search', {
        body: { query, jobs: allJobs }
      });

      if (error) {
        console.error('AI search error:', error);
        toast.error('KI-Suche fehlgeschlagen. Versuche es erneut.');
        return allJobs;
      }

      const result = data as AISearchResult;
      
      if (!result.matchedJobIds || result.matchedJobIds.length === 0) {
        setReasoning('Keine passenden Jobs gefunden. Versuche eine andere Suchanfrage.');
        setConfidence(0);
        toast.info('Keine passenden Jobs gefunden');
        return [];
      }

      setReasoning(result.reasoning);
      setConfidence(result.confidence);

      const matchedJobs = allJobs.filter(job => 
        result.matchedJobIds.includes(job.id)
      );

      toast.success(`${matchedJobs.length} ${matchedJobs.length === 1 ? 'Job' : 'Jobs'} gefunden`);
      
      return matchedJobs;
    } catch (error) {
      console.error('AI search error:', error);
      toast.error('KI-Suche fehlgeschlagen');
      return allJobs;
    } finally {
      setIsSearching(false);
    }
  };

  const clearSearch = () => {
    setReasoning('');
    setConfidence(0);
  };

  return {
    searchJobs,
    isSearching,
    reasoning,
    confidence,
    clearSearch,
  };
};
